import React, { useState, createContext, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'

import { DEFAULT_DATE_RANGE_SELECTION, dateSelectionItems } from './DateRangeSelector'
import use311Query from '../../util/use311Query'
import { useGetNewServiceRequestsQuery, useGetClosedServiceRequestsQuery } from '../../util/service-requests-api'
import moment from 'moment'

export const ThreeOneOneDataContext = createContext()

const DEFAULT_ACTIVE_GROUP = 'new'

// use query params
function useQuery () {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}
const ThreeOneOneDataHandler = ({
  areaOfInterest,
  children
}) => {
  const query = useQuery()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [popupData, setPopupData] = useState()

  // pull the date range from query params, or use the default ('last7days')
  const buildDateSelection = () => {
    const value = query.get('dateSelection')
    if (value === 'custom') {
      const dateFrom = query.get('dateFrom')
      const dateTo = query.get('dateTo')
      if (dateFrom && dateTo) {
        return {
          value: 'custom',
          displayName: 'Custom range',
          dateRange: [moment(dateFrom).startOf('day'), moment(dateTo).endOf('day')]
        }
      }
    }
    return dateSelectionItems.find((d) => d.value === value) || DEFAULT_DATE_RANGE_SELECTION
  }
  const [dateSelection, setDateSelection] = useState(buildDateSelection)

  // pull the active group from query params, or use the default ('new')
  const activeGroupFromQueryParams = query.get('group') || DEFAULT_ACTIVE_GROUP
  const [activeGroup, setActiveGroup] = useState(activeGroupFromQueryParams)

  const [categoryFilter, setCategoryFilter] = useState()

  // use use311Query multiple times for the various different cuts of data we need
  const {
    data: newServiceRequests,
    isLoading: newIsLoading
  } = use311Query(useGetNewServiceRequestsQuery, {
    areaOfInterest,
    dateSelection
  })

  const {
    data: closedServiceRequests,
    isLoading: closedIsLoading
  } = use311Query(useGetClosedServiceRequestsQuery, {
    areaOfInterest,
    dateSelection
  })

  // react to changes in query params to update date range
  useEffect(() => {
    setDateSelection(buildDateSelection())
  }, [query.get('dateSelection'), query.get('dateFrom'), query.get('dateTo')])

  // when data selection changes, update the query params
  const handleDateSelectionChange = (d) => {
    query.set('dateSelection', d.value)
    if (d.value === 'custom' && d.dateRange) {
      query.set('dateFrom', d.dateRange[0].format('YYYY-MM-DD'))
      query.set('dateTo', d.dateRange[1].format('YYYY-MM-DD'))
    } else {
      query.delete('dateFrom')
      query.delete('dateTo')
    }
    navigate({
      pathname,
      search: query.toString(),
      hash: window.location.hash // get this value directly from window because mapboxgl is updating the hash
    })
  }

  const handleActiveGroupChange = (activeGroup) => {
    query.set('group', activeGroup)
    setActiveGroup(activeGroup)
    navigate({
      pathname,
      search: query.toString(),
      hash: window.location.hash // get this value directly from window because mapboxgl is updating the hash
    })
  }

  const isLoading = newIsLoading || closedIsLoading

  // cacluate counts for simple display in the UI (for tabs)
  const groupCounts = {
    new: newServiceRequests?.features.length,
    closed: closedServiceRequests?.features.length
  }

  // export only one slice of the data at a time depending on activeGroup
  let serviceRequests = newServiceRequests
  if (activeGroup === 'closed') {
    serviceRequests = closedServiceRequests
  }

  // filter by rollupCategory
  if (categoryFilter) {
    const filteredServiceRequests = {
      ...serviceRequests,
      features: serviceRequests.features.filter(d => d.properties.rollupCategory === categoryFilter)
    }
    serviceRequests = filteredServiceRequests
  }

  return (
    <ThreeOneOneDataContext.Provider value={{
      serviceRequests,
      groupCounts,
      closedServiceRequests,
      dateSelection,
      handleDateSelectionChange,
      popupData,
      setPopupData,
      activeGroup,
      handleActiveGroupChange,
      categoryFilter,
      setCategoryFilter,
      isLoading
    }}
    >
      {children}
    </ThreeOneOneDataContext.Provider>
  )
}

ThreeOneOneDataHandler.propTypes = {
  areaOfInterest: PropTypes.object,
  children: PropTypes.array
}

export default ThreeOneOneDataHandler
